import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { PAN_SCENARIOS } from "@/data/pan-card-scenarios";

const TITLE = "บริการขอ PAN Card อินเดีย (Permanent Account Number) จากประเทศไทย | NYC";
const DESC = "บริการยื่นขอ PAN Card ของอินเดีย (Form 49A / 49AA) ครบวงจรจากประเทศไทย — สำหรับ NRI, OCI, ชาวต่างชาติ — ปรึกษาฟรี ประเมินงานให้ทันที ทักไลน์ได้เลย";

const steps = [
  { name: "ประเมินความจำเป็น", text: "ระบุเหตุผลที่ต้องใช้ PAN (ภาษี ลงทุน ธุรกิจ มรดก) เพื่อเลือก Form 49A/49AA ให้ถูก" },
  { name: "เตรียมเอกสาร", text: "พาสปอร์ต, address proof, รูปถ่าย, ลายเซ็น — ทีมช่วยจัดเตรียมตามข้อกำหนด NSDL/UTIITSL" },
  { name: "แปล + Notary + Apostille", text: "เอกสารต่างชาติต้องผ่าน Notary + Apostille (Hague 15 ธ.ค. 2025) หรือ Embassy Legalization" },
  { name: "ยื่นผ่าน NSDL/UTIITSL", text: "ยื่นออนไลน์กับ Protean (NSDL e-Gov) หรือ UTIITSL — ทีมติดตามสถานะทุก 3 วัน" },
  { name: "รับ e-PAN + บัตรจริง", text: "e-PAN (PDF) ภายใน 3–5 วันหลังยื่น; บัตรจริงส่งจากอินเดียมาไทย 15–25 วันทำการ" },
];

const faqs = [
  { q: "PAN Card คืออะไร?", a: "Permanent Account Number — เลข 10 หลักที่กรมสรรพากรอินเดีย (Income Tax Department) ออกให้ ใช้เป็น Tax ID สำหรับทุกธุรกรรมทางการเงินในอินเดีย ตั้งแต่เปิดบัญชี ลงทุน จนถึงยื่นภาษี" },
  { q: "ต่างจาก Aadhaar อย่างไร?", a: "Aadhaar คือเลขประจำตัวสำหรับผู้พำนักในอินเดียเท่านั้น — PAN ออกได้แม้ไม่พำนักในอินเดีย (NRI/OCI/ต่างชาติ)" },
  { q: "ต้องบินไปอินเดียไหม?", a: "ไม่ต้องบิน — Form 49AA สำหรับ NRI, OCI และชาวต่างชาติ ยื่นออนไลน์ผ่าน NSDL/Protean ได้จากไทยทั้งหมด เอกสารต้นฉบับส่งด่วน DHL Express ไปศูนย์ประมวลผลที่มุมไบหรือปูเน ทีมงาน NYC ในกรุงเทพติดตามสถานะและประสานงานทุกขั้นตอนแทนคุณจนได้บัตร" },
  { q: "ใช้เวลานานแค่ไหน?", a: "e-PAN (PDF ลงลายเซ็นดิจิทัล) ออกภายใน 3–5 วันทำการหลัง NSDL ตรวจเอกสารครบ ใช้แทนบัตรจริงได้ทันทีสำหรับเปิดบัญชีธนาคารและ KYC; บัตรจริงแบบเคลือบส่ง Speed Post จากอินเดียมาไทยอีก 15–25 วัน หากเลือก DHL upgrade เหลือ 7–10 วัน" },
  { q: "ค่าใช้จ่ายทั้งหมด?", a: "Form fee ~₹1,020 (NRI) + ค่าแปล/Notary/Apostille + ค่าบริการ NYC — แพ็กเกจรวมเริ่ม ฿3,500 (Reprint หมายเลขเดิม) ถึง ฿15,000 (บริษัท/Trust/HUF) ทุกราคาแจ้งครบก่อนเริ่มงาน ไม่มีค่าใช้จ่ายแอบแฝง มี invoice ภาษีเต็มรูปแบบ ผ่อนบัตรเครดิต 0% ได้" },
  { q: "ต้องมี Indian address ไหม?", a: "ไม่จำเป็นสำหรับ NRI, OCI cardholders หรือต่างชาติ (Form 49AA) — ใช้ overseas address ที่ไทยได้ทันที บัตร PAN จะจัดส่งไปที่อยู่ต่างประเทศตามที่ระบุในแบบฟอร์ม เอกสารพิสูจน์ที่อยู่ในไทย (ทะเบียนบ้านเหลือง, work permit, สัญญาเช่า) เราแปล+รับรอง+Apostille ให้ครบตามเกณฑ์ NSDL" },
  { q: "ใช้ทำอะไรได้บ้าง?", a: "เปิดบัญชี NRO/NRE, ลงทุน mutual fund/stock ผ่าน demat, ซื้อ-ขายอสังหาฯ, ยื่น ITR (Income Tax Return), รับเงินปันผล/royalty, ขอ Tax Residency Certificate ตาม DTAA India–Thailand" },
  { q: "หมดอายุไหม?", a: "PAN ตัวเลข 10 หลักไม่หมดอายุตลอดชีพ แต่ผู้พำนักในอินเดียต้อง link กับ Aadhaar ตามกำหนด CBDT มิฉะนั้น PAN จะกลายเป็น 'inoperative' และถูกหัก TDS 20% ทันที หากเปลี่ยนชื่อ วันเกิด ลายเซ็น หรือที่อยู่ ต้องยื่น Form 49A Correction เพื่อรักษาสถานะ active" },
  { q: "หายทำอย่างไร?", a: "ยื่น Reprint of PAN ผ่าน Form 49A Correction — แพ็กเกจ NYC เริ่ม ฿3,500 ได้บัตรใหม่หมายเลข 10 หลักเดิมภายใน 10–15 วันทำการ หากบัตรจริงหายในต่างประเทศ ขอ e-PAN duplicate ทางอีเมลได้ภายใน 48 ชั่วโมงเพื่อใช้เป็นหลักฐานชั่วคราวกับธนาคารและ KYC" },
];

export const Route = createFileRoute("/pan-card/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/pan-card", "th")}, { name: "description", content: ctaDesc(DESC, "/pan-card", "th")},
      { name: "keywords", content: "PAN Card, Permanent Account Number, NRI PAN, OCI PAN, Form 49A, Form 49AA, PAN Card Thailand, ขอ PAN อินเดีย" },
      { property: "og:title", content: ctaTitle(TITLE, "/pan-card", "th")}, { property: "og:description", content: ctaDesc(DESC, "/pan-card", "th")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/pan-card" },
    ],
    links: canonicalAndHreflang("/pan-card", "/en/pan-card"),
    scripts: buildLandingSchemas({
      path: "/pan-card", title: TITLE, description: DESC,
      serviceType: "PAN Card Application Service (India)", priceFromThb: 3500, steps, faqs,
      breadcrumbs: [{ name: "Home", path: "/" }, { name: "บริการ", path: "/services" }, { name: "PAN Card", path: "/pan-card" }],
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <ServiceLanding
        badge="India · Income Tax Dept."
        title="PAN Card อินเดีย — ยื่นจากไทยครบวงจร"
        subtitle={DESC}
        quickAnswer={{
          q: "ขอ PAN Card อินเดียจากไทยได้ไหม?",
          a: "ได้ — สำหรับ NRI, OCI, ชาวต่างชาติ, บริษัท และ Trust ใช้ Form 49AA ยื่นผ่าน NSDL ออนไลน์จากไทย ไม่ต้องบินไปอินเดีย เริ่ม ฿3,500 พร้อมบริการแปล+Notary+Apostille ครบในแพ็กเกจเดียว e-PAN 3–5 วัน · บัตรจริง 15–25 วัน",
        }}
        heroBullets={[
          "ยื่นจากไทย ไม่ต้องบินไปอินเดีย",
          "รองรับทุก scenario (NRI/OCI/ต่างชาติ/บริษัท/Trust/Reprint)",
          "แปล + Notary + Apostille ครบในแพ็กเกจเดียว",
          "ทีมติดตามสถานะ NSDL ทุก 3 วัน",
        ]}
        whoFor={{
          th: ["คนอินเดียที่พำนักในไทย (NRI)", "คนไทยเชื้อสายอินเดีย (OCI)", "ผู้ลงทุน mutual fund/หุ้นอินเดีย", "บริษัทไทยที่ทำธุรกิจกับอินเดีย"],
          foreign: ["Foreign nationals with Indian income", "MNCs opening Indian branch"],
        }}
        steps={steps}
        pricing={PAN_SCENARIOS.map((s) => ({ label: `${s.th} (Form ${s.form})`, price: `${s.priceFrom.toLocaleString()}+`, note: s.turnaround }))}
        faqs={faqs}
        relatedLinks={[
          { to: "/tax/dta/india", label: "DTAA อินเดีย–ไทย" },
          { to: "/apostille/india", label: "Apostille อินเดีย" },
          { to: "/notary", label: "Notary Public" },
          { to: "/translate/government", label: "แปลเอกสารราชการ" },
          { to: "/services", label: "บริการทั้งหมด" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold">เลือกประเภทผู้ยื่น</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PAN_SCENARIOS.map((s) => (
            <Link key={s.slug} to="/pan-card/$scenario" params={{ scenario: s.slug }}
              className="rounded border p-4 hover:border-primary">
              <div className="font-semibold">{s.th}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.en} · Form {s.form}</div>
              <div className="text-xs mt-2">เริ่ม ฿{s.priceFrom.toLocaleString()} · {s.turnaround}</div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
